import { createListCollection, DialogRoot, Input, Textarea, useBreakpointValue } from '@chakra-ui/react';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldRow } from '@/components/ui/field';
import { Divider } from '@/components/ui/Divider';
import { useForm, FormProvider, Controller, useWatch, Path } from 'react-hook-form';
import {
    DialogActionTrigger,
    DialogBody,
    DialogCloseTrigger,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValueText } from '@/components/ui/select';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { SaveTemplateDto, TemplateDto } from '@/lib/ServerApi';
import { NumberInputField, NumberInputRoot } from '@/components/ui/number-input';
import { capitalize } from '@/utils/capitalize';
import { disciplines, problemTypes, taskTypes, templateTypeOptions } from '@/utils/coding-details';

const disciplineOptions = createListCollection({
    items: disciplines,
});

const durationOptions = createListCollection({
    items: [
        { value: 'days', label: 'days' },
        { value: 'weeks', label: 'weeks' },
    ],
});

const typeOptions = createListCollection({
    items: templateTypeOptions,
});

const problemTypeOptions = createListCollection({
    items: problemTypes,
});

const taskTypeOptions = createListCollection({
    items: taskTypes,
});

const templateModalValidations = z
    .object({
        templateType: z.string({ invalid_type_error: 'required' }).min(1, 'required'),
        title: z.string({ invalid_type_error: 'required' }).min(1, 'required'),
        description: z.string({ invalid_type_error: 'required' }).min(1, 'required'),
        discipline: z.string().nullish(),
        days: z.number().nullish(),
        frequency: z.string().nullish(),
        type: z.string().nullish(),
    })
    .superRefine((data, ctx) => {
        const required = (path: string) =>
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [path],
                message: 'required',
            });

        if (data.templateType === 'goal') {
            if (!data.discipline) {
                required('discipline');
            }

            if (!data.days) {
                required('days');
            }
            if (!data.frequency) {
                required('frequency');
            }
        }

        if (data.templateType === 'task') {
            if (!data.type) {
                required('type');
            }
        }
    });

interface TeachingProps {
    onClose: () => void;
    onAdd: (e: SaveTemplateDto) => void;
    isSaving?: boolean;
}

export const TemplateModal = ({ onClose, onAdd, isSaving = false }: TeachingProps) => {
    const isMobile = useBreakpointValue({ base: true, md: false });
    const rhf = useForm<SaveTemplateDto>({
        resolver: zodResolver(templateModalValidations),
        reValidateMode: 'onChange',
        mode: 'onChange',
    });

    const {
        control,
        register,
        setValue,
        handleSubmit,
        formState: { errors },
    } = rhf;

    const templateType = useWatch({ control: rhf.control, name: 'templateType' });

    return (
        <DialogRoot lazyMount size="lg" open onExitComplete={onClose} onOpenChange={onClose} closeOnInteractOutside={false}>
            <FormProvider {...rhf}>
                <DialogContent colorPalette="purple">
                    <form
                        noValidate
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            return handleSubmit((e) => {
                                onAdd(e);
                            })(e);
                        }}>
                        <DialogHeader>
                            <DialogTitle>Add Template</DialogTitle>
                        </DialogHeader>

                        <DialogBody>
                            <FieldGroup>
                                <Divider />
                                <FieldRow>
                                    <Field
                                        label="Template type"
                                        required
                                        flex="0 1 250px"
                                        invalid={!!errors.templateType}
                                        errorText={errors.templateType?.message}>
                                        <Controller
                                            control={control}
                                            name="templateType"
                                            render={({ field }) => (
                                                <SelectRoot
                                                    name={field.name}
                                                    value={[field.value || '']}
                                                    onValueChange={({ value }) => {
                                                        field.onChange(value[0]);

                                                        for (const prop of ['discipline', 'days', 'frequency', 'type']) {
                                                            setValue(
                                                                prop as Path<
                                                                    Pick<TemplateDto, 'discipline' | 'days' | 'frequency' | 'type'>
                                                                >,
                                                                undefined,
                                                            );
                                                        }
                                                    }}
                                                    onInteractOutside={() => field.onBlur()}
                                                    collection={typeOptions}>
                                                    <SelectTrigger>
                                                        <SelectValueText />
                                                    </SelectTrigger>
                                                    <SelectContent zIndex={9999}>
                                                        {typeOptions.items.map((type) => (
                                                            <SelectItem key={type.value} item={type}>
                                                                {type.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </SelectRoot>
                                            )}
                                        />
                                    </Field>
                                </FieldRow>
                                <FieldRow>
                                    <Field
                                        label="Title"
                                        flex="0 1 250px"
                                        required
                                        invalid={!!errors.title}
                                        errorText={errors.title?.message}>
                                        <Input type="text" {...register('title')} />
                                    </Field>
                                    {templateType === 'goal' ? (
                                        <Field
                                            label="Discipline"
                                            required
                                            flex="0 1 250px"
                                            invalid={!!errors.discipline}
                                            errorText={errors.discipline?.message}>
                                            <Controller
                                                control={control}
                                                name="discipline"
                                                render={({ field }) => (
                                                    <SelectRoot
                                                        name={field.name}
                                                        value={[field.value || '']}
                                                        onValueChange={({ value }) => {
                                                            field.onChange(value[0]);
                                                        }}
                                                        onInteractOutside={() => field.onBlur()}
                                                        collection={disciplineOptions}>
                                                        <SelectTrigger>
                                                            <SelectValueText />
                                                        </SelectTrigger>
                                                        <SelectContent zIndex={9999}>
                                                            {disciplineOptions.items.map((disc) => (
                                                                <SelectItem item={disc} key={disc.value}>
                                                                    {disc.label.toUpperCase()}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </SelectRoot>
                                                )}
                                            />
                                        </Field>
                                    ) : undefined}
                                    {['task', 'problem'].includes(templateType) ? (
                                        <Field
                                            label={`${capitalize(templateType)} type`}
                                            required
                                            flex="0 1 250px"
                                            invalid={!!errors.type}
                                            errorText={errors.type?.message}>
                                            <Controller
                                                control={control}
                                                name="type"
                                                render={({ field }) => (
                                                    <SelectRoot
                                                        name={field.name}
                                                        value={[field.value || '']}
                                                        onValueChange={({ value }) => {
                                                            field.onChange(value[0]);
                                                        }}
                                                        onInteractOutside={() => field.onBlur()}
                                                        collection={templateType === 'task' ? taskTypeOptions : problemTypeOptions}>
                                                        <SelectTrigger>
                                                            <SelectValueText />
                                                        </SelectTrigger>
                                                        <SelectContent zIndex={9999}>
                                                            {(templateType === 'task' ? taskTypeOptions : problemTypeOptions).items.map(
                                                                (type) => (
                                                                    <SelectItem item={type} key={type.value}>
                                                                        {type.label}
                                                                    </SelectItem>
                                                                ),
                                                            )}
                                                        </SelectContent>
                                                    </SelectRoot>
                                                )}
                                            />
                                        </Field>
                                    ) : undefined}
                                </FieldRow>
                                {templateType === 'goal' ? (
                                    <FieldRow>
                                        <Field
                                            label="Duration"
                                            required
                                            flex={isMobile ? '1' : '0 1 150px'}
                                            invalid={!!errors.days}
                                            errorText={errors.days?.message}>
                                            <Controller
                                                control={control}
                                                name="days"
                                                render={({ field }) => (
                                                    <NumberInputRoot
                                                        size="sm"
                                                        disabled={field.disabled}
                                                        name={field.name}
                                                        value={field.value as unknown as string}
                                                        onValueChange={({ value }) => field.onChange(value ? parseInt(value) : undefined)}>
                                                        <NumberInputField onBlur={field.onBlur} />
                                                    </NumberInputRoot>
                                                )}
                                            />
                                        </Field>
                                        <Field
                                            required
                                            alignSelf="flex-end"
                                            flex={isMobile ? '1' : '0 1 150px'}
                                            invalid={!!errors.frequency}
                                            errorText={errors.frequency?.message}>
                                            <Controller
                                                control={control}
                                                name="frequency"
                                                render={({ field }) => (
                                                    <SelectRoot
                                                        name={field.name}
                                                        value={[field.value || '']}
                                                        onValueChange={({ value }) => {
                                                            field.onChange(value[0]);
                                                        }}
                                                        onInteractOutside={() => field.onBlur()}
                                                        collection={durationOptions}>
                                                        <SelectTrigger>
                                                            <SelectValueText />
                                                        </SelectTrigger>
                                                        <SelectContent zIndex={9999}>
                                                            {durationOptions.items.map((duration) => (
                                                                <SelectItem item={duration} key={duration.value}>
                                                                    {duration.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </SelectRoot>
                                                )}
                                            />
                                        </Field>
                                    </FieldRow>
                                ) : undefined}
                                <FieldRow>
                                    <Field
                                        label="Description"
                                        required
                                        invalid={!!errors.description}
                                        errorText={errors.description?.message}>
                                        <Textarea {...register('description')} height={40} />
                                    </Field>
                                </FieldRow>
                            </FieldGroup>
                        </DialogBody>
                        <DialogFooter>
                            <DialogActionTrigger asChild>
                                <Button variant="outline" onClick={onClose}>
                                    Cancel
                                </Button>
                            </DialogActionTrigger>
                            <Button type="submit" loading={isSaving} loadingText="Adding">
                                Add Template
                            </Button>
                        </DialogFooter>
                        <DialogCloseTrigger />
                    </form>
                </DialogContent>
            </FormProvider>
        </DialogRoot>
    );
};
